import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CardWatchLogo, CardWatchMark } from "@/components/CardWatchLogo";
import { ShieldCheck, Landmark, LineChart, Sparkles, ArrowRight, Lock, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Feature = ({ icon: Icon, title, desc, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: [0.2, 0.65, 0.3, 1] }}
    className="group relative bg-[#0E172C] border border-[#1A2849] rounded-2xl p-8 hover:border-[#00E676]/50 hover:-translate-y-1 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_0_40px_-10px_rgba(0,230,118,0.35)]"
    data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="w-11 h-11 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center mb-5">
      <Icon size={20} className="text-[#00E676]" />
    </div>
    <h3 className="text-white text-xl font-semibold mb-2 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {title}
    </h3>
    <p className="text-slate-300 text-sm leading-relaxed">{desc}</p>
  </motion.div>
);

const Landing = () => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Landing CTAs:
  //   Anonymous visitors → /signup (they must sign up before adding cards)
  //   Signed-in users    → /dashboard, where the "Link credit card" button
  //                        opens the two-way chooser (Automatic Sync vs Manual)
  const handleConnectBank = async () => {
    if (!user) {
      navigate("/signup");
      return;
    }
    // Deep-link so the Dashboard auto-opens the chooser dialog on mount.
    navigate("/dashboard?add=1");
  };

  const openApp = () => navigate(user ? "/dashboard" : "/signup");
  const openDemo = () => navigate("/demo");

  return (
    <div className="min-h-screen bg-[#060B19] text-white overflow-x-hidden">
      {/* Ambient grid + glows */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#00E676 1px, transparent 1px), linear-gradient(90deg, #00E676 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden="true"
      />
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/10 blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/40 blur-[160px]" />

      {/* Nav */}
      <header className="relative z-10 bg-[#060B19]/70 backdrop-blur-xl border-b border-white/5">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link">
            <CardWatchLogo size={38} />
          </Link>
          <nav className="hidden md:flex items-center gap-9 text-sm text-slate-300">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">
              Features
            </a>
            <a href="#security" className="hover:text-white transition-colors" data-testid="nav-security">
              Security
            </a>
            <Link to="/demo" className="hover:text-white transition-colors" data-testid="nav-dashboard">
              Demo
            </Link>
          </nav>
          <button
            onClick={openApp}
            data-testid="nav-open-app"
            className="hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-white/10 hover:border-[#00E676]/50 hover:bg-[#00E676]/5 text-sm transition-colors"
          >
            {user ? "Open App" : "Sign up"} <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pt-24 lg:pt-32 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/5 px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-[#00E676] font-bold"
              data-testid="hero-badge"
            >
              <Sparkles size={12} /> Bank-grade linking via Plaid
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-7 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="hero-title"
            >
              Every Credit Card.
              <br />
              One <span className="text-[#00E676]">CardWatch</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              A premium hub to track credit card balances, due dates, and optimize utilization in one private dashboard.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <button
                onClick={handleConnectBank}
                disabled={loading}
                data-testid="hero-connect-bank-button"
                className="group inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-7 py-3.5 text-sm transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_-10px_rgba(0,230,118,0.6)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Opening bank selector…" : "Link a credit card"}
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <Link
                to="/demo"
                data-testid="hero-try-demo-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 px-7 py-3.5 text-sm text-slate-200 hover:text-white transition-colors"
              >
                Try the demo dashboard
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex items-center gap-6 text-xs text-slate-300"
            >
              <div className="flex items-center gap-2" data-testid="trust-berlin-group">
                <Lock size={13} /> AISP-regulated data flow
              </div>
              <div className="flex items-center gap-2" data-testid="trust-banks">
                <Globe size={13} /> 5,000+ issuers · 50+ countries
              </div>
            </motion.div>
          </div>

          {/* 3D Card mockup */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 24, rotateX: 8 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.65, 0.3, 1] }}
              style={{ transformStyle: "preserve-3d", perspective: 1200 }}
              className="relative mx-auto max-w-md"
            >
              <div
                className="relative rounded-3xl p-8 border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
                style={{
                  background:
                    "linear-gradient(135deg, #1E3A8A 0%, #0F1E4A 55%, #060B19 100%)",
                }}
              >
                <div className="flex items-center justify-between">
                  <CardWatchMark size={52} />
                  <span
                    className="text-[10px] uppercase tracking-[0.3em] text-[#00E676] font-bold"
                    data-testid="mockup-live-badge"
                  >
                    Live
                  </span>
                </div>
                <div className="mt-10">
                  <div className="text-xs text-slate-300 uppercase tracking-widest">Total balance</div>
                  <div
                    className="mt-2 text-4xl font-bold tracking-tight"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="mockup-balance"
                  >
                    £48,213<span className="text-slate-300 text-2xl">.55</span>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                    <div className="text-slate-300">Cards</div>
                    <div className="text-white font-semibold mt-1">6</div>
                  </div>
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                    <div className="text-slate-300">Issuers</div>
                    <div className="text-white font-semibold mt-1">4</div>
                  </div>
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                    <div className="text-slate-300">Utilization</div>
                    <div className="text-[#00E676] font-semibold mt-1">22%</div>
                  </div>
                </div>
              </div>
              <div className="absolute -inset-6 -z-10 rounded-[2rem] blur-3xl opacity-40 bg-[#00E676]/20" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E676] font-bold">Why CardWatch</div>
          <h2
            className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Every balance, watched — <span className="text-slate-300">never blinked.</span>
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Feature
            icon={Landmark}
            title="Any card, any issuer"
            desc="Track every Visa, Mastercard, and Amex across issuers and countries in a single, private dashboard."
            delay={0}
          />
          <Feature
            icon={LineChart}
            title="Utilization in motion"
            desc="Per-card and overall utilization ratios, live due-date reminders, and optimization suggestions to keep your score healthy."
            delay={0.08}
          />
          <Feature
            icon={ShieldCheck}
            title="Bank-grade linking via Plaid"
            desc="Regulated, read-only access. CardWatch never sees or stores your card credentials — Plaid handles the vault."
            delay={0.16}
          />
        </div>
      </section>

      {/* Security band */}
      <section id="security" className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="relative overflow-hidden rounded-3xl border border-[#1A2849] bg-[#0E172C] p-10 lg:p-14">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#00E676]/10 blur-3xl" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.25em] text-[#00E676] font-bold">
                Bank-grade linking via Plaid
              </div>
              <h3
                className="mt-4 text-3xl lg:text-4xl font-semibold tracking-tight text-white"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Your credentials never touch our servers.
              </h3>
              <p className="mt-4 text-slate-300 max-w-xl leading-relaxed">
                Every connection is initiated through Plaid's regulated open banking gateway. CardWatch receives only read-only balance and transaction data — never passwords, never card numbers.
              </p>
              <button
                onClick={handleConnectBank}
                disabled={loading}
                data-testid="security-connect-bank-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-6 py-3 text-sm transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? "Opening Plaid…" : "Link your first credit card"}
                <ArrowRight size={15} />
              </button>
            </div>
            <div className="lg:col-span-5">
              <ul className="space-y-4 text-sm text-slate-300">
                {[
                  "PSD2-regulated read-only access",
                  "OAuth-based bank authentication",
                  "TLS 1.3 + AES-256 encrypted vault",
                  "You revoke any connection with one click",
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3"
                    data-testid={`security-item-${i}`}
                  >
                    <ShieldCheck size={16} className="mt-0.5 text-[#00E676]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 mt-10">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-300">
          <div className="flex items-center gap-3">
            <CardWatchMark size={26} />
            <span>© {new Date().getFullYear()} CardWatch. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6">
            <span data-testid="footer-saltedge">Powered by Plaid</span>
            <a href="#security" className="hover:text-white transition-colors">Security</a>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
              Privacy
            </Link>
            <a
              href="mailto:privacy@cardwatchapp.com"
              className="hover:text-white transition-colors"
              data-testid="footer-contact-link"
            >
              Contact
            </a>
            <Link to="/dashboard" className="hover:text-white transition-colors" data-testid="footer-dashboard-link">
              Dashboard
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
